import React, {useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import type {AttendanceDayRecord} from '@/entities/me/types';
import {DayLog} from '@/features/view-day-log/ui/DayLog';
import {Card} from '@/shared/ui/card';
import {Icon} from '@/shared/ui/icons';
import {StatusPill} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {fonts} from '@/shared/ui/fonts';
import {radii, spacing, useTheme} from '@/shared/ui/theme';
import {
  formatClock,
  formatDayOfMonth,
  formatHoursFromMinutes,
} from '@/shared/lib/format';
import {flagLabelKey, flagTone, knownFlags} from '../lib/flags';

/**
 * One day of the month grid. The date is a fixed-width block (mono, so the digits align
 * down the list); the body carries the arrival→departure pair and the worked total; the
 * verdict flags trail as status pills. A day off with nothing on it (`scheduled: false`,
 * no events) is dimmed — present in the grid so the month is whole, but visibly not a
 * working day.
 *
 * The date block is a tinted square rather than a bare column: scrolling a month, the
 * eye tracks that left rail of blocks and never has to re-find where a row starts.
 *
 * A day with events opens: under the pair sits that day's own journal — every event the
 * terminals recorded for this person, in order. The pair is a CONCLUSION («09:14 →
 * 18:02»); the journal is the record it was drawn from, down to which door and which
 * direction each scan was filed as. «Почему приход в 9:14» is a question the pair
 * cannot answer and the list under it usually can.
 *
 * Deliberately NOT the manager card (`view-attendance/PersonDayList`): the in→out
 * episodes and the two competing durations answer a payroll question nobody asks about
 * their own month. The events do get asked about.
 */
const styles = StyleSheet.create({
  row: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  chevron: {transform: [{rotate: '90deg'}]},
  chevronOpen: {transform: [{rotate: '270deg'}]},
  journal: {
    marginTop: spacing(3),
    paddingTop: spacing(3),
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  restful: {opacity: 0.55},
  date: {
    width: 46,
    paddingVertical: spacing(1),
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayNum: {fontFamily: fonts.mono.medium, fontSize: 18, lineHeight: 22},
  body: {flex: 1, gap: 2},
  flags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing(1),
    justifyContent: 'flex-end',
    flexShrink: 1,
    maxWidth: '42%',
  },
});

export function DayRow({
  day,
  companyId,
}: {
  day: AttendanceDayRecord;
  /**
   * Absent — the row stays a plain row. The journal needs a company to filter by, and a
   * screen without one has nothing to open.
   */
  companyId?: string;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const [open, setOpen] = useState(false);

  const flags = knownFlags(day.flags);
  const restful = !day.scheduled && day.event_count === 0;
  // A day the terminals never saw has no journal to show — the row does not pretend to
  // open onto an empty list.
  const expandable = !!companyId && day.event_count > 0;

  const arrival = formatClock(day.arrival_time_millis);
  const departure = formatClock(day.departure_time_millis);
  const hasPair = day.arrival_time_millis != null || day.departure_time_millis != null;
  const worked =
    day.worked_minutes != null
      ? formatHoursFromMinutes(
          day.worked_minutes,
          t('attendance.units.hour'),
          t('attendance.units.minute'),
        )
      : null;

  return (
    <Card padding={3} variant={restful ? 'flat' : 'default'} style={restful && styles.restful}>
      <Pressable
        accessibilityRole={expandable ? 'button' : undefined}
        accessibilityState={expandable ? {expanded: open} : undefined}
        onPress={expandable ? () => setOpen(v => !v) : undefined}
        testID={`my-day-${day.date}`}
        style={styles.row}>
        <View style={[styles.date, {backgroundColor: p.muted}]}>
          <AppText style={styles.dayNum}>{formatDayOfMonth(day.date)}</AppText>
          <AppText variant="micro">{t(`attendance.weekday.${day.weekday}`)}</AppText>
        </View>

        <View style={styles.body}>
          <AppText variant="monoLg" color={hasPair ? p.foreground : p.mutedForeground}>
            {hasPair ? `${arrival} → ${departure}` : '—'}
          </AppText>
          {worked ? (
            <AppText variant="caption">
              {t('attendance.worked', {value: worked})}
            </AppText>
          ) : null}
        </View>

        {flags.length > 0 ? (
          <View style={styles.flags}>
            {flags.map(f => (
              <StatusPill
                key={f}
                tone={flagTone(f)}
                size="sm"
                dot={false}
                label={t(flagLabelKey(f))}
              />
            ))}
          </View>
        ) : null}

        {/* A row that opens has to look like one — the same chevron the manager card
            uses, for the same reason: an affordance nobody sees is not one. */}
        {expandable ? (
          <View style={open ? styles.chevronOpen : styles.chevron}>
            <Icon name="chevron" size={16} color={p.mutedForeground} />
          </View>
        ) : null}
      </Pressable>

      {open && companyId ? (
        <View style={[styles.journal, {borderTopColor: p.border}]}>
          <DayLog
            companyId={companyId}
            date={day.date}
            expectedCount={day.event_count}
          />
        </View>
      ) : null}
    </Card>
  );
}
